import { Box, Tooltip } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'

import CustomChip from '@/components/Chip'
import HtmlKonvaElement from '@/components/HtmlKonvaElement'
import PieChart from '@/components/PieChart'
import { useAppSelector } from '@/hooks/redux'
import { getInitials } from '@/pages/stainingStudio/utils'
import { STUMP_SHADE_BACKGROUNDS } from '@/utils/constants'
import { getTooltipPlacement } from '@/utils/helper'

import LabelValuePair from './LabelValuePair'

const lastTooltipMap: Record<string, { x: number; y: number }> = {}

const TooltipContent = React.memo(({ output }: { output: any }) => {
  return (
    <Box
      display="flex"
      flexGrow={1}
      flexDirection="column"
      gap={1}
      alignItems="flex"
    >
      <Box>
        {output?.provided_input?.natural_die_material && (
          <>
            <Box sx={{ width: '100%' }}></Box>
            <LabelValuePair
              value={
                <Box
                  sx={{
                    wordBreak: 'break-word',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {output.provided_input.natural_die_material?.toUpperCase() ||
                    ''}
                </Box>
              }
            />
          </>
        )}
      </Box>
      {output?.prep_ratios?.length && (
        <>
          <Box sx={{ borderTop: '1px solid #999', width: '100%' }} />
          <PieChart
            labels={output?.prep_names}
            values={output?.prep_ratios}
            height={'78px'}
            width={'78px'}
          />
        </>
      )}
    </Box>
  )
})

const StumpShadeTooltip = React.memo(
  ({
    keyName,
    data,
    output,
    open,
    toggle,
    keyData,
    onDelete,
  }: {
    keyName: string
    data: any
    output: any
    open: boolean
    toggle: (key: string) => void
    close: (key: string) => void
    onDelete: (keyName: string) => void
    keyData: string
  }) => {
    const [placement, setPlacement] = useState<'right' | 'left'>('right')

    const canvasImage = useAppSelector((state) => state.app.canvasImage)

    const prevOutputRef = useRef<any>(null)

    useEffect(() => {
      const currentX = data.imagePosition?.x
      const currentY = data.imagePosition?.y

      const placementResult = getTooltipPlacement({
        keyName,
        currentX,
        currentY,
        open,
        canvasImageId: canvasImage?.id,
        output,
        prevOutputRef,
        lastTooltipMap,
      })

      if (placementResult) {
        setPlacement(placementResult)
      }
    }, [
      open,
      keyName,
      data.imagePosition?.x,
      data.imagePosition?.y,
      output,
      canvasImage?.id,
    ])
    return (
      <HtmlKonvaElement
        key={keyName}
        x={data.imagePosition?.x}
        y={data.imagePosition?.y}
      >
        <Box position="relative">
          <Box sx={{ transform: 'translate(-50%, -60%)' }}>
            <Tooltip
              open={open}
              title={output ? <TooltipContent output={output} /> : null}
              arrow
              placement={placement}
              slotProps={{
                popper: { disablePortal: true },
                tooltip: {
                  sx: {
                    bgcolor: 'rgba(33, 33, 33, 0.80)',
                    color: 'white',
                    padding: '10px',
                  },
                },
                arrow: { sx: { color: 'rgba(33, 33, 33, 0.80)' } },
              }}
            >
              <CustomChip
                onClick={() => toggle(keyName)}
                sx={{
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: 'black',
                  padding: '4px 8px',
                  background: STUMP_SHADE_BACKGROUNDS[keyName],
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: '20px',
                  '&:hover': {
                    background: STUMP_SHADE_BACKGROUNDS[keyName],
                  },
                  '& .MuiChip-label': {
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 8px 0 0px',
                    lineHeight: '20px',
                  },
                }}
                label={getInitials(keyData)}
                onDelete={() => onDelete(keyName)}
              />
            </Tooltip>
          </Box>
        </Box>
      </HtmlKonvaElement>
    )
  },
)

export default StumpShadeTooltip
